import React, { useState, useEffect } from 'react';
import { Plant, plantsAPI } from '../services/api';
import { Link } from 'react-router-dom';
import {
  Container,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Box,
  CircularProgress,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  Paper,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  Login as LoginIcon,
  LocalFlorist as PlantIcon,
  Visibility as ViewIcon,
  CalendarToday as DateIcon
} from '@mui/icons-material';

const PublicPlantBrowser: React.FC = () => {
  const [plants, setPlants] = useState<Plant[]>([]);
  const [filteredPlants, setFilteredPlants] = useState<Plant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'family' | 'date'>('name');
  const [filterBy, setFilterBy] = useState<'all' | 'published'>('published');

  useEffect(() => {
    loadPlants();
  }, []);

  // Filter and sort plants based on search term and sort option
  useEffect(() => {
    let filtered = plants;

    // Filter by status
    if (filterBy === 'published') {
      filtered = plants.filter(plant => plant.status === 'published');
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(plant => 
        plant.primary_scientific_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        plant.family.toLowerCase().includes(searchTerm.toLowerCase()) ||
        plant.genus?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        plant.species?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort plants
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.primary_scientific_name.localeCompare(b.primary_scientific_name);
        case 'family':
          return a.family.localeCompare(b.family);
        case 'date':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        default:
          return 0;
      }
    });

    setFilteredPlants(filtered);
  }, [plants, searchTerm, sortBy, filterBy]);

  const loadPlants = async () => {
    try {
      setLoading(true);
      const response = await plantsAPI.getPlants();
      setPlants(response.plants);
    } catch (err: any) {
      setError(err.message || 'Failed to load plants');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        bgcolor="grey.50"
      >
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          🌿 Loading plant collection...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          Error: {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={loadPlants}
          startIcon={<PlantIcon />}
          fullWidth
        >
          Try Again
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: 'grey.50', minHeight: '100vh' }}>
      {/* Header */}
      <Paper 
        elevation={3} 
        sx={{ 
          textAlign: 'center', 
          py: 6, 
          mb: 4,
          background: 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)',
          color: 'white'
        }}
      >
        <Container maxWidth="md">
          <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
            <PlantIcon sx={{ fontSize: 60, mr: 2 }} />
            <Typography variant="h2" component="h1" fontWeight="bold">
              PalmGarden
            </Typography>
          </Box>
          <Typography variant="h5" sx={{ mb: 3, opacity: 0.9 }}>
            Discover and explore our comprehensive collection of botanical specimens
          </Typography>
          <Button
            component={Link}
            to="/login"
            variant="contained"
            size="large"
            startIcon={<LoginIcon />}
            sx={{
              bgcolor: 'white',
              color: 'primary.main',
              '&:hover': {
                bgcolor: 'grey.100'
              }
            }}
          >
            Admin Login
          </Button>
        </Container>
      </Paper>

      <Container maxWidth="lg">
        {/* Search and Filter Controls */}
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Grid container spacing={3} alignItems="end">
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Search Plants"
                placeholder="Search by name, family, genus, or species..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                variant="outlined"
              />
            </Grid>
            
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'name' | 'family' | 'date')}
                  label="Sort By"
                  startAdornment={<SortIcon sx={{ mr: 1, color: 'text.secondary' }} />}
                >
                  <MenuItem value="name">Scientific Name</MenuItem>
                  <MenuItem value="family">Family</MenuItem>
                  <MenuItem value="date">Date Added</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Filter</InputLabel>
                <Select
                  value={filterBy}
                  onChange={(e) => setFilterBy(e.target.value as 'all' | 'published')}
                  label="Filter"
                  startAdornment={<FilterIcon sx={{ mr: 1, color: 'text.secondary' }} />}
                >
                  <MenuItem value="published">Published Only</MenuItem>
                  <MenuItem value="all">All Plants</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box textAlign="center" mt={2}>
            <Chip 
              label={`Showing ${filteredPlants.length} of ${plants.length} plants`}
              color="primary"
              variant="outlined"
            />
          </Box>
        </Paper>

        {/* Plants Grid */}
        <Grid container spacing={3}>
          {filteredPlants.length === 0 ? (
            <Grid size={{ xs: 12 }}>
              <Paper elevation={2} sx={{ p: 6, textAlign: 'center' }}>
                {searchTerm ? (
                  <>
                    <Typography variant="h5" color="text.secondary" gutterBottom>
                      🔍 No plants found
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                      No plants match your search for "{searchTerm}"
                    </Typography>
                    <Button 
                      variant="contained" 
                      onClick={() => setSearchTerm('')}
                      startIcon={<SearchIcon />}
                    >
                      Clear Search
                    </Button>
                  </>
                ) : (
                  <Typography variant="h6" color="text.secondary">
                    No plants available
                  </Typography>
                )}
              </Paper>
            </Grid>
          ) : (
            filteredPlants.map((plant) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={plant.id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Typography variant="h6" component="h3" color="primary" fontWeight="bold">
                        {plant.primary_scientific_name}
                      </Typography>
                      <Chip 
                        label={plant.status}
                        size="small"
                        color={plant.status === 'published' ? 'success' : 'warning'}
                        variant="outlined"
                      />
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
                      {plant.family}
                    </Typography>

                    <Grid container spacing={1} sx={{ mb: 2 }}>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" color="text.secondary">
                          Genus:
                        </Typography>
                        <Typography variant="body2">
                          {plant.genus || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" color="text.secondary">
                          Species:
                        </Typography>
                        <Typography variant="body2">
                          {plant.species || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" color="text.secondary">
                          Size:
                        </Typography>
                        <Typography variant="body2">
                          {plant.size_category || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" color="text.secondary">
                          IUCN:
                        </Typography>
                        <Typography variant="body2">
                          {plant.iucn_status || 'N/A'}
                        </Typography>
                      </Grid>
                    </Grid>
                    
                    {plant.description && (
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          mb: 2,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {plant.description}
                      </Typography>
                    )}
                  </CardContent>

                  <Divider />
                  
                  <CardActions sx={{ justifyContent: 'space-between', p: 2 }}>
                    <Box display="flex" alignItems="center">
                      <DateIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        {new Date(plant.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Button 
                      size="small" 
                      variant="contained" 
                      startIcon={<ViewIcon />}
                      sx={{ minWidth: 'auto' }}
                    >
                      View
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))
          )}
        </Grid>

        {/* Footer */}
        <Paper 
          elevation={2} 
          sx={{ 
            mt: 6, 
            p: 4, 
            textAlign: 'center',
            background: 'linear-gradient(135deg, #f5f5f5 0%, #e8f5e8 100%)'
          }}
        >
          <Typography variant="h5" color="primary" gutterBottom>
            🌿 PalmGarden Botanical Collection
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            A comprehensive database of botanical specimens for researchers, enthusiasts, and nature lovers.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            © 2024 PalmGarden. All rights reserved.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default PublicPlantBrowser;
